import type {
  KYCQuestionDefinition,
  KYCQuestionnaireElementLayout,
} from '@anchorage/graphql/generated';
import { KYCQuestionType } from '@anchorage/graphql/generated';
import type { CustomLinkProps } from '@anchorage/utils';
import { parseSanitize } from '@anchorage/utils';
import { reactHookForm } from '@latitude/form';
import { Icon } from '@latitude/icons';
import { Text } from '@latitude/text';
import { Tooltip } from '@latitude/tooltip';
import cn from 'classnames';
import { type FormAnswer } from 'hooks/useQuestionnaire/types';
import { useQuestionnaire } from 'hooks/useQuestionnaire/useQuestionnaire';
import { relatedQuestionAnsweredOptionIds } from 'hooks/useQuestionnaire/utils';
import type { ComponentType } from 'react';
import { useEffect, useMemo } from 'react';

import { GroupIcon } from '../Group/GroupIcon';
import { QuestionNote } from '../QuestionNote';
import { CardListComponent } from './components/CardListComponent';
import { CheckboxComponent } from './components/CheckboxComponent';
import { DateComponent } from './components/DateComponent';
import { DropdownComponent } from './components/DropdownComponent';
import { RadioComponent } from './components/RadioComponent';
import { RadioGroupComponent } from './components/RadioGroupComponent';
import {
  type RenderTaxCertification,
  type TaxCertificationCardProps,
  TaxCertificationComponent,
} from './components/TaxCertificationComponent';
import { TextareaComponent } from './components/TextareaComponent';
import { TextFieldComponent } from './components/TextFieldComponent';
import { UploadComponent } from './components/UploadComponent/UploadComponent';

const CONTAINER_CLASSES =
  'relative flex flex-col gap-spacing-sys-spacing-4 text-sys-size-14 [&:not(:first-child)]:mt-spacing-sys-spacing-16';

type Props = {
  question: KYCQuestionDefinition;
  isDisabled?: boolean;
  answeredOptions?: Record<string, string>;
  groupNameAndIndex?: string;
  CustomLink?: ComponentType<CustomLinkProps>;
  isBackoffice?: boolean;
  layout?: KYCQuestionnaireElementLayout;
  allQuestions?: KYCQuestionDefinition[];
  trackFn?: (questionId: string) => void;
  showTriggered?: boolean;
  formID?: string;
  renderTaxCertification?: RenderTaxCertification;
};

function Question({
  question,
  isDisabled = false,
  answeredOptions,
  groupNameAndIndex,
  CustomLink,
  isBackoffice,
  layout,
  allQuestions,
  trackFn = () => {},
  showTriggered = false,
  formID,
  renderTaxCertification,
}: Props) {
  const { useFormContext } = reactHookForm;
  const { setValue, getValues } = useFormContext();
  const { shouldShowElement } = useQuestionnaire();

  const shouldShow = useMemo(
    () =>
      shouldShowElement?.({
        triggerOptionID: question.triggerOptionID,
        answeredOptions,
        questions: allQuestions,
        groupNameAndIndex,
      }),
    [
      question.triggerOptionID,
      shouldShowElement,
      answeredOptions,
      groupNameAndIndex,
      allQuestions,
    ],
  );

  const isValidQuestionType = useMemo(
    () =>
      [
        KYCQuestionType.CHECKBOX,
        KYCQuestionType.DATE,
        KYCQuestionType.DROPDOWN,
        KYCQuestionType.DROPDOWN_MULTI_SELECT,
        KYCQuestionType.GROUP,
        KYCQuestionType.RADIO,
        KYCQuestionType.RADIO_GROUP,
        KYCQuestionType.TEXT_AREA,
        KYCQuestionType.UPLOAD,
        KYCQuestionType.TEXTFIELD,
        KYCQuestionType.CARD_SELECT,
        KYCQuestionType.TAX_CERTIFICATION,
      ].includes(question.type),
    [question.type],
  );

  const questionComponent = useMemo(() => {
    switch (question.type) {
      case KYCQuestionType.CHECKBOX:
        return (
          <CheckboxComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            trackFn={trackFn}
          />
        );
      case KYCQuestionType.DATE:
        return (
          <DateComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
          />
        );
      case KYCQuestionType.DROPDOWN:
        return (
          <DropdownComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            isBackoffice={isBackoffice}
            freeformOptionId={question.freeformOptionID}
            CustomLink={CustomLink}
            trackFn={trackFn}
          />
        );
      case KYCQuestionType.DROPDOWN_MULTI_SELECT:
        return (
          <DropdownComponent
            isDisabled={isDisabled || question.readOnly}
            isMulti
            question={question}
            isBackoffice={isBackoffice}
            CustomLink={CustomLink}
            trackFn={trackFn}
          />
        );
      case KYCQuestionType.RADIO:
        return (
          <RadioComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            CustomLink={CustomLink}
            trackFn={trackFn}
          />
        );
      case KYCQuestionType.RADIO_GROUP:
        return (
          <RadioGroupComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
          />
        );
      case KYCQuestionType.TEXT_AREA:
        return (
          <TextareaComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            textAreaSize={layout?.textAreaSize}
            CustomLink={CustomLink}
            trackFn={trackFn}
          />
        );
      case KYCQuestionType.UPLOAD:
        return (
          <UploadComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            CustomLink={CustomLink}
            isBackoffice={isBackoffice}
            trackFn={trackFn}
            formID={formID}
          />
        );
      case KYCQuestionType.CARD_SELECT:
        return (
          <CardListComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            trackFn={trackFn}
          />
        );
      case KYCQuestionType.TAX_CERTIFICATION: {
        const renderCard = ({
          onOpen,
          isComplete,
        }: TaxCertificationCardProps) => (
          <TaxCertificationComponent
            title={layout?.groupTemplate?.newItemTemplate ?? ''}
            description={layout?.groupTemplate?.newItemDescription ?? ''}
            ctaLabel={layout?.groupTemplate?.addDetailsButtonTitle ?? ''}
            iconName={GroupIcon(layout?.groupTemplate?.icon ?? '')}
            isComplete={isComplete}
            isDisabled={isDisabled || question.readOnly}
            onOpen={onOpen}
          />
        );
        return renderTaxCertification
          ? renderTaxCertification({ questionID: question.id, renderCard })
          : renderCard({ onOpen: () => {}, isComplete: false });
      }
      default:
        return (
          <TextFieldComponent
            isDisabled={isDisabled || question.readOnly}
            question={question}
            CustomLink={CustomLink}
            trackFn={trackFn}
          />
        );
    }
  }, [
    question,
    isDisabled,
    CustomLink,
    isBackoffice,
    layout?.textAreaSize,
    layout?.groupTemplate?.newItemTemplate,
    layout?.groupTemplate?.newItemDescription,
    layout?.groupTemplate?.addDetailsButtonTitle,
    layout?.groupTemplate?.icon,
    trackFn,
    formID,
    renderTaxCertification,
  ]);

  // if this question has relatedQuestionIDs, we want to copy the answer of this question to the related questions
  useEffect(() => {
    if (shouldShow) {
      const relatedQuestionIDs =
        (question.relatedQuestionIDs as string[]) || [];
      const answer = getValues(question.id) as FormAnswer;
      const answeredOptionIds: string[] | string = answer?.options || '';

      // we go through every related question and copy the answer of this question to the related question
      // if it has a valid equivalent
      relatedQuestionIDs.forEach((relatedQuestionID) => {
        let relatedQuestionAnswer = answer;

        // if there is answered options, we need to filter the invalid options of the related question
        if (answeredOptionIds.length) {
          const relatedQuestionPossibleOptions =
            allQuestions?.find(
              (relatedQuestion) => relatedQuestion.id === relatedQuestionID,
            )?.options || [];

          const relatedQuestionOptions = relatedQuestionAnsweredOptionIds(
            answeredOptionIds,
            question.options,
            relatedQuestionPossibleOptions,
          );

          relatedQuestionAnswer = {
            ...answer,
            options: relatedQuestionOptions,
          };

          // we only copy the answer if the related question has equivalent options
          // if not, we leave it as is
          relatedQuestionOptions.length &&
            setValue(`${relatedQuestionID}`, relatedQuestionAnswer);
        } else {
          setValue(`${relatedQuestionID}`, relatedQuestionAnswer);
        }
      });
    }
  }, [
    shouldShow,
    question.id,
    setValue,
    question.relatedQuestionIDs,
    getValues,
    allQuestions,
    question.options,
  ]);

  if (!shouldShow && !showTriggered) {
    return null;
  }

  if (isBackoffice && question.type === KYCQuestionType.TAX_CERTIFICATION) {
    return null;
  }

  if (!isValidQuestionType) {
    return (
      <div className={CONTAINER_CLASSES}>
        <Text color="danger">Error reading the questionnaire</Text>
      </div>
    );
  }

  return (
    <div
      className={cn(CONTAINER_CLASSES, question.hidden && 'hidden')}
      data-question-id={question.id}
    >
      <div className="flex items-center">
        {question?.text && (
          <Text variant="bodyMedium" color="strong">
            {question?.text}
          </Text>
        )}
        {question?.tooltip && (
          <Tooltip
            color="light"
            text={<Text variant="captionMedium">{question?.tooltip}</Text>}
            side="bottom"
            clampLines={false}
            className="text-left"
          >
            <Icon
              name="info-circle"
              className="ml-sizing-sys-size-8 h-sizing-sys-size-16 w-sizing-sys-size-16 align-top"
            />
          </Tooltip>
        )}
      </div>
      {question?.header && (
        <Text
          className="text-color-comp-select-helper-text-enabled"
          variant="captionMedium"
        >
          {parseSanitize(question?.header, CustomLink)}
        </Text>
      )}
      {question?.note && <QuestionNote note={question.note} />}
      {questionComponent}
      {question.optional && (
        <Text
          className="absolute right-0 top-spacing-sys-spacing-4 text-color-comp-text-field-helper-text-enabled"
          variant="captionMedium"
        >
          Optional
        </Text>
      )}
    </div>
  );
}

export { Question };
